<script lang="ts" context="module">
  type PageEntry = number | '...';

  export function getPageItems(
    current: number,
    max: number | undefined,
    maxPagesToShow: number,
  ): PageEntry[] {
    let pages: PageEntry[] = [];
    if (max != undefined) {
      if (max <= maxPagesToShow) {
        pages = [...rangeInclude(1, max)];
      } else {
        const pagesAroundEllipsis = maxPagesToShow - 1;
        const entriesAroundOne = Math.round(pagesAroundEllipsis / 2);
        const entriesAroundLast = max - Math.floor(pagesAroundEllipsis / 2);

        if (current < entriesAroundOne || entriesAroundLast < current) {
          pages = [
            ...rangeInclude(1, entriesAroundOne),
            '...',
            ...rangeInclude(entriesAroundLast + 1, max),
          ];
        } else {
          const entriesWithoutEllipsisAndBothEnd = maxPagesToShow - 2 - 2;
          let start = current - Math.floor(entriesWithoutEllipsisAndBothEnd / 2);
          let end = current + Math.round(entriesWithoutEllipsisAndBothEnd / 2);

          pages = [1, '...', ...rangeInclude(start, end), '...', max];
        }
      }
    }
    return pages;
  }
</script>

<script lang="ts">
  import { goto } from '$app/navigation';

  import { range, rangeInclude } from '$lib/utils/array';
  import { ChevronLeft, ChevronRight } from 'lucide-svelte';

  export let current = 1;
  export let max: number | undefined = undefined;
  export let maxPagesToShow = 10;
  export let getHref: (page: number) => string;
  export let showEntries: { perPage: number; total: number } | undefined = undefined;

  $: pages = getPageItems(current, max, maxPagesToShow);

  function prev() {
    if (current > 1) {
      goto(getHref(current - 1));
    }
  }

  function next() {
    if (max != undefined && current < max) {
      goto(getHref(current + 1));
    }
  }
</script>

<div class="pagination">
  {#if showEntries}
    <p>
      <strong>{showEntries.total}</strong>개 중
      <strong>{showEntries.perPage * (current - 1) + 1}</strong>
      ~ <strong>{Math.min(showEntries.perPage * current, showEntries.total)}</strong>번째 표시 중
    </p>
  {/if}
  <section>
    <button class="none" disabled={current === 1} on:click={prev}><ChevronLeft size={16} /></button>
    {#each pages as pageNumberOrEllipsis, i}
      {#if pageNumberOrEllipsis === '...'}
        <div>{pageNumberOrEllipsis}</div>
      {:else}
        <a aria-current={pageNumberOrEllipsis === current} href={getHref(pageNumberOrEllipsis)}>
          {pageNumberOrEllipsis}
        </a>
      {/if}
    {/each}
    <button class="none" disabled={current === max} on:click={next}
      ><ChevronRight size={16} /></button
    >
  </section>
</div>

<style lang="postcss">
  .pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-6);

    p {
      font-size: var(--size-5);
    }

    section {
      display: flex;
      font-size: var(--size-7);
      border-radius: var(--radius-2);
      overflow: hidden;
      ---border: 1px solid var(--border-normal);
      border: var(---border);
      width: fit-content;

      * {
        min-width: 4rem;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      *:not(:last-child) {
        border-right: var(---border);
      }
    }
  }

  a {
    padding: var(--space-3);
    text-decoration: none;

    &:hover {
      transition:
        background-color 0.1s ease-out,
        color 0.1s ease-out;
      background-color: var(--mauve-a4);
    }

    &[aria-current='true'] {
      background-color: var(--text);
      color: var(--background);
    }
  }

  button {
    min-width: 3.2rem;
    &:first-child {
      border-radius: 0;
      border-top-left-radius: var(--radius-2);
      border-bottom-left-radius: var(--radius-2);
    }
    &:last-child {
      border-radius: 0;
      border-top-right-radius: var(--radius-2);
      border-bottom-right-radius: var(--radius-2);
    }
  }
</style>
